import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  EMAIL_TEMPLATE_LEADERSHIP,
  EMAIL_TEMPLATE_PE_BOARD,
  PACK_GENERIC,
  buildDefaultEmailHtml,
  normalizeEmailPack,
  previewNameFromParticipants,
} from "./inviteEmailTemplates";
import { getAdminUser } from "../api/adminClient";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const TEMPLATE_OPTIONS = [
  {
    id: EMAIL_TEMPLATE_LEADERSHIP,
    label: "CEO & Leadership Team",
  },
  {
    id: EMAIL_TEMPLATE_PE_BOARD,
    label: "PE & Board Participants",
  },
];

function templateLabel(id) {
  return TEMPLATE_OPTIONS.find((o) => o.id === id)?.label ?? "";
}

function sectionEmailPack() {
  const user = getAdminUser();
  if (user?.email_pack === PACK_GENERIC || user?.tenant_id) return PACK_GENERIC;
  return normalizeEmailPack(user?.email_pack);
}

function sectionProductName() {
  const user = getAdminUser();
  return (
    user?.product_display_name?.trim() ||
    user?.tenant_name?.trim() ||
    "Team Assessment"
  );
}

/**
 * Toggleable email template + editor (step 2 of invite wizard).
 */
export default function InviteEmailSection({
  participants = [],
  recipientEmails = [],
  selectedTemplate,
  onSelectTemplate,
  editorHtml,
  onEditorChange,
  autoSuggestedTemplate = null,
  templateIsManual = false,
  expanded = false,
  onToggleExpanded,
  disabled = false,
  hideSendButton = false,
  sendDisabled = false,
  onManualSelect,
  onSend,
  sending = false,
  sendLabel = "Send invites",
  compact = false,
  assessmentMode = "lite_to_full",
}) {
  const applyTemplate = (id, manual = true) => {
    if (manual) onManualSelect?.();
    onSelectTemplate(id);
    const { firstName, lastName } = previewNameFromParticipants(participants);
    onEditorChange(
      buildDefaultEmailHtml(id, { firstName, lastName }, {
        assessmentMode,
        pack: sectionEmailPack(),
        productName: sectionProductName(),
      })
    );
  };

  const emails =
    recipientEmails.length > 0
      ? recipientEmails
      : participants.map((p) => p.email?.trim()).filter(Boolean);

  const showAutoHint =
    !templateIsManual &&
    autoSuggestedTemplate != null &&
    selectedTemplate === autoSuggestedTemplate;

  const activeLabel = templateLabel(selectedTemplate);
  const visibleTemplates =
    sectionEmailPack() === PACK_GENERIC
      ? TEMPLATE_OPTIONS.filter((o) => o.id === EMAIL_TEMPLATE_LEADERSHIP)
      : TEMPLATE_OPTIONS;

  return (
    <section className={compact ? "space-y-3" : "border-t pt-6 mt-6 space-y-4"}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className={`font-semibold text-brand ${compact ? "text-sm" : ""}`}>
            Invitation email
          </h3>
          {!expanded ? (
            <p className="text-sm text-gray-600 mt-1">
              {activeLabel ? (
                <>
                  Using template: <span className="font-medium text-gray-800">{activeLabel}</span>
                  {showAutoHint ? " (from participant roles)" : null}
                </>
              ) : (
                "Template is selected from participant roles when you add participants."
              )}
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-0.5">
              Choose a template and edit the message before sending.
            </p>
          )}
        </div>
        {onToggleExpanded ? (
          <button
            type="button"
            className="text-sm text-[#004f73] font-medium hover:underline shrink-0 text-left"
            onClick={() => onToggleExpanded(!expanded)}
          >
            {expanded ? "Hide email customization" : "Customize invitation email"}
          </button>
        ) : null}
      </div>

      {expanded ? (
        <>
          <div
            className="flex flex-col sm:flex-row rounded-lg border border-gray-200 p-1 bg-gray-50 gap-1 sm:gap-0"
            role="group"
            aria-label="Email template"
          >
            {visibleTemplates.map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={disabled}
                className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-md transition text-center ${
                  selectedTemplate === opt.id
                    ? "bg-[#004f73] text-white shadow-sm"
                    : "text-gray-700 hover:bg-white"
                }`}
                onClick={() => applyTemplate(opt.id, true)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {showAutoHint ? (
            <p className="text-xs text-[#004f73] bg-[#e8f4f8] border border-[#004f73]/20 rounded-lg px-3 py-2">
              Auto-selected {templateLabel(autoSuggestedTemplate)} based on participant roles.
            </p>
          ) : null}
        </>
      ) : null}

      {expanded && selectedTemplate != null && editorHtml ? (
        <>
          <p className="text-xs text-gray-500 mb-2">
            Preview matches how the invitation is laid out in the sent email (link is added
            when you send).
          </p>
          <div className={disabled ? "opacity-60 pointer-events-none" : ""}>
            <ReactQuill
              theme="snow"
              value={editorHtml}
              onChange={onEditorChange}
              modules={quillModules}
              className="invite-email-quill bg-white"
            />
          </div>
          <p className="text-xs text-gray-500">
            The assessment link is appended automatically when the email is sent.
          </p>
        </>
      ) : null}

      {!hideSendButton ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border">
          <span className="text-gray-700 text-sm truncate">
            {emails.length
              ? emails.join(", ")
              : "Recipient emails appear here"}
          </span>
          <button
            type="button"
            className="blue_btn shrink-0 px-6 disabled:opacity-50"
            disabled={sendDisabled || sending || selectedTemplate == null}
            onClick={onSend}
          >
            {sending ? "Sending…" : sendLabel}
          </button>
        </div>
      ) : null}
    </section>
  );
}
